import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AnimatedValue } from '@/components/ui/animated-value';
import { IncompleteProductsList } from '@/components/dashboard/IncompleteProductsList';
import { DataCompletenessCard } from '@/components/dashboard/DataCompletenessCard';
import { ProductStatusChart } from '@/components/dashboard/ProductStatusChart';
import { MostMissingAttributesCard } from '@/components/dashboard/MostMissingAttributesCard';
import { CompletenessTrendCard } from '@/components/dashboard/CompletenessTrendCard';
import { RecentActivityCard } from '@/components/dashboard/RecentActivityCard';
import { config } from '@/config/config';
import { toast } from 'sonner';
import { Plus, Package, AlertTriangle, RefreshCcw, Activity as ActivityIcon } from 'lucide-react';
import { ROUTES } from '@/config/routes';
import { useDashboardTranslations } from '@/hooks/useDashboardTranslations';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { useErrorHandler } from '@/utils/errorHandler';

// Get recent products count from config
const RECENT_PRODUCTS_COUNT = config.dashboard.display.recentProductsCount;

/**
 * Format currency based on configuration
 */
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: config.dashboard.display.defaultCurrency,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Map activity action to an icon
 */
const getActivityIcon = (action: string) => {
  switch (action.toLowerCase()) {
    case 'create':
      return <Plus className="size-4 text-green-500" />;
    case 'update':
      return <RefreshCcw className="size-4 text-blue-500" />;
    case 'delete':
      return <AlertTriangle className="size-4 text-red-500" />;
    default:
      return <ActivityIcon className="size-4 text-gray-500" />;
  }
};

interface AnimatedDivProps {
  delay?: number;
  className?: string;
  children: React.ReactNode;
}

const AnimatedDiv = ({ children, delay = 0, className = '' }: AnimatedDivProps) => {
  return (
    <div
      className={`animate-fadeIn ${className}`}
      style={{
        animationDelay: `${delay}ms`,
        animationFillMode: 'both',
      }}
    >
      {children}
    </div>
  );
};

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: { summary, activity, incompleteProducts },
    loading,
    error,
    fetchAll,
  } = useDashboardData();
  const { user } = useAuth();
  const i18n = useDashboardTranslations();
  const { handleError } = useErrorHandler();

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Handle KPI card click (navigate to filtered products)
  const handleKpiClick = (filter: string) => {
    navigate(`${ROUTES.APP.PRODUCTS}?${filter}`);
  };

  // Refresh dashboard data
  const handleRefresh = async () => {
    try {
      await fetchAll(true); // Force refresh
      toast.success(i18n.getToastText('refreshSuccess'));
    } catch (error) {
      handleError(
        error as Error,
        {
          component: 'DashboardPage',
          action: 'handleRefresh',
          userId: user?.id,
          organizationId: user?.organization_id,
        },
        {
          toastMessage: i18n.getToastText('refreshError'),
        }
      );
    }
  };

  return (
    <>
      {/* Header with welcome message and actions */}
      <div
        className="mb-2 flex flex-col justify-between gap-6 py-2 sm:flex-row sm:items-center"
        data-tour-target="tour-complete"
      >
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {i18n.getHeaderText('welcomeBack', {
              name: user?.name || i18n.getHeaderText('defaultUser'),
            })}
          </h1>
          <p className="mt-1 text-muted-foreground">{i18n.getHeaderText('overview')}</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="enterprise-button-secondary"
            onClick={handleRefresh}
            disabled={loading.summary || loading.activity || loading.incompleteProducts}
            aria-label={i18n.getAriaText('refreshButton')}
          >
            <RefreshCcw
              className={`mr-2 size-4 ${Object.values(loading).some(Boolean) ? 'animate-spin' : ''}`}
            />
            {i18n.getHeaderText('refreshData')}
          </Button>
          <PermissionGuard permission="product.add">
            <Button
              onClick={() => navigate(ROUTES.APP.NEW)}
              className="enterprise-button"
              aria-label={i18n.getAriaText('newProductButton')}
            >
              <Plus className="mr-2 size-4" />
              {i18n.getHeaderText('newProduct')}
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Top KPIs Row */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <AnimatedDiv delay={0} className="h-full min-h-[320px]">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Card
                  className="enterprise-card-elevated h-full"
                  tabIndex={0}
                  aria-label={i18n.getTotalProductsText('ariaLabel')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      {i18n.getTotalProductsText('title')}
                    </CardTitle>
                    <Package className="size-4 text-primary" />
                  </CardHeader>
                  <CardContent className="pb-4">
                    {loading.summary ? (
                      <Skeleton className="h-8 w-16" />
                    ) : (
                      <>
                        <div className="text-2xl font-bold text-foreground">
                          <AnimatedValue value={summary?.total_products || 0} />
                        </div>
                        <hr className="my-2 border-border" />
                        {summary?.recent_products?.length ? (
                          <div className="mb-1 mt-2 text-xs font-medium text-muted-foreground">
                            {i18n.getTotalProductsText('lastProducts', {
                              count: RECENT_PRODUCTS_COUNT,
                            })}
                          </div>
                        ) : null}
                        <div className="mt-4 space-y-2 text-sm text-foreground">
                          {Array.from({ length: RECENT_PRODUCTS_COUNT }).map((_, i) => {
                            const p = summary?.recent_products?.slice(0, RECENT_PRODUCTS_COUNT)[i];
                            return p ? (
                              <div
                                key={p.id}
                                className="enterprise-focus flex cursor-pointer items-center justify-between gap-2 rounded border-b border-border px-2 py-1.5 transition last:border-b-0 hover:bg-muted/50 focus:outline-none"
                                tabIndex={0}
                                role="button"
                                aria-label={i18n.getAriaText('productName', {
                                  name: p.name || p.sku,
                                })}
                                onClick={e => {
                                  e.stopPropagation();
                                  navigate(ROUTES.buildProductDetailUrl(p.id));
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    navigate(ROUTES.buildProductDetailUrl(p.id));
                                  }
                                }}
                              >
                                <div
                                  className="flex w-2/5 items-center truncate font-medium"
                                  title={p.name}
                                >
                                  {p.name || i18n.getTotalProductsText('unnamed')}
                                </div>
                                <div
                                  className="flex w-[30%] items-center truncate text-gray-500 dark:text-muted-foreground"
                                  title={p.sku}
                                >
                                  <span className="mr-1 text-gray-400 dark:text-muted-foreground">
                                    #
                                  </span>
                                  {p.sku}
                                </div>
                                <div
                                  className="flex w-[30%] items-center truncate text-xs italic text-gray-400 dark:text-muted-foreground"
                                  title={p.family?.name}
                                >
                                  <span className="mr-1">🏷</span>
                                  {p.family?.name || '–'}
                                </div>
                              </div>
                            ) : (
                              <div
                                key={i}
                                className="flex items-center justify-between gap-2 rounded border-b border-gray-100 p-2 italic text-gray-200 last:border-b-0 dark:border-border dark:text-muted-foreground"
                              >
                                <div className="w-2/5">
                                  {i18n.getTotalProductsText('noProduct')}
                                </div>
                                <div className="w-[30%]">&nbsp;</div>
                                <div className="w-[30%]">&nbsp;</div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent side="top">{i18n.getTotalProductsText('tooltip')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </AnimatedDiv>
        <AnimatedDiv delay={100} className="h-full min-h-[320px]">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="h-full">
                  <DataCompletenessCard
                    completeness={summary?.data_completeness || 0}
                    mostMissingFields={summary?.most_missing_fields || []}
                    loading={loading.summary}
                    attributesMissingCount={summary?.attributes_missing_count || 0}
                    mandatoryAttributes={summary?.mandatory_attributes || []}
                    familyCompleteness={summary?.family_completeness || []}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">{i18n.getDataCompletenessText('tooltip')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </AnimatedDiv>
        <AnimatedDiv delay={200} className="h-full min-h-[320px]">
          <ProductStatusChart
            activeProducts={summary?.active_products || 0}
            inactiveProducts={summary?.inactive_products || 0}
            loading={loading.summary}
          />
        </AnimatedDiv>
      </div>

      {/* Row 2: Most Missing Attributes and Required Attributes */}
      <div className="mb-4 grid auto-rows-fr grid-cols-1 items-stretch gap-x-2 gap-y-1 lg:grid-cols-2">
        <AnimatedDiv delay={300} className="h-full min-h-[380px]">
          <MostMissingAttributesCard
            mostMissingFields={summary?.most_missing_fields || []}
            loading={loading.summary}
          />
        </AnimatedDiv>
        <AnimatedDiv delay={350} className="h-full min-h-[380px]">
          <CompletenessTrendCard data={summary?.completeness_trend} loading={loading.summary} />
        </AnimatedDiv>
      </div>

      {/* Row 3: Incomplete Products and Recent Activity side by side */}
      <div className="grid grid-cols-1 gap-x-2 gap-y-1 lg:grid-cols-2">
        <div className="min-h-[400px]">
          <AnimatedDiv delay={400}>
            <IncompleteProductsList
              products={incompleteProducts}
              loading={loading.incompleteProducts}
              title={i18n.getIncompleteProductsText('title')}
              description={i18n.getIncompleteProductsText('description')}
              maxItems={5}
            />
          </AnimatedDiv>
        </div>
        <div className="min-h-[400px]">
          <AnimatedDiv delay={450}>
            <RecentActivityCard activities={activity} loading={loading.activity} maxItems={10} />
          </AnimatedDiv>
        </div>
      </div>
    </>
  );
};
